'use client'

import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    MdArrowBack,
    MdClose,
    MdHistory,
    MdInventory2,
    MdSearch,
    MdSwapVert,
    MdWarning,
} from 'react-icons/md'
import { useAuth } from '@/store/hooks'
import {
    CatalogStockItem,
    CatalogStockMovement,
    CatalogStockMovementReason,
    MANUAL_STOCK_REASONS,
    STOCK_MOVEMENT_REASON_LABELS,
    createCatalogStockMovement,
    getCatalogStock,
    getCatalogStockMovements,
} from '@/lib/api/catalog/catalog-stock-api'
import {
    currencyFormat,
    parseCurrencyToCents,
} from '@/lib/api/catalog/catalog-price-format'

const PAGE_SIZE = 25

/**
 * Estoque dos PRODUTOS do catálogo: saldo, reservado, disponível e o extrato que explica
 * cada mudança.
 *
 * O saldo antes só era digitado no cadastro do produto, sem histórico — uma diferença no
 * fim do mês não tinha como ser investigada. Aqui toda entrada e saída tem autor, motivo e
 * data.
 *
 * ⚠️ "Reservado" é o que compras da loja seguram entre fechar o carrinho e o pagamento
 * confirmar. É por isso que Disponível pode ser menor que o Saldo sem que nada tenha saído
 * da prateleira.
 */
export default function CatalogStockPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const [searchParams] = useSearchParams()

    const [items, setItems] = useState<CatalogStockItem[]>([])
    const [total, setTotal] = useState(0)
    const [page, setPage] = useState(1)
    const [loading, setLoading] = useState(true)
    const [search, setSearch] = useState('')
    const [onlyLow, setOnlyLow] = useState(false)

    const [movementTarget, setMovementTarget] = useState<CatalogStockItem | null>(
        null
    )
    const [historyTarget, setHistoryTarget] = useState<CatalogStockItem | null>(
        null
    )

    const load = useCallback(async () => {
        if (!user?.companyId) return
        setLoading(true)
        try {
            const response = await getCatalogStock({
                companyId: user.companyId,
                search: search.trim() || undefined,
                onlyLow,
                skip: (page - 1) * PAGE_SIZE,
                take: PAGE_SIZE,
            })
            if (response.success) {
                setItems(response.result.data)
                setTotal(response.result.total)
            } else {
                toast.error(response.error || 'Erro ao carregar o estoque')
            }
        } catch {
            toast.error('Erro ao carregar o estoque')
        } finally {
            setLoading(false)
        }
    }, [user?.companyId, search, onlyLow, page])

    useEffect(() => {
        load()
    }, [load])

    // O aviso de estoque mínimo leva para cá com o produto na URL: abrir o extrato dele
    // direto poupa o operador de procurá-lo na lista.
    useEffect(() => {
        const fromUrl = searchParams.get('catalogId')
        if (!fromUrl || items.length === 0) return
        const found = items.find(item => item.catalogId === Number(fromUrl))
        if (found) setHistoryTarget(found)
        // Só na primeira carga que já tem itens.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items.length])

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE))

    return (
        <div className="flex flex-col gap-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <button
                        className="btn btn-ghost btn-sm btn-circle"
                        onClick={() =>
                            navigate('/registrations/catalog?category=PRODUCT')
                        }
                        title="Voltar para os produtos"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-primary/70 flex items-center justify-center shadow-lg">
                        <MdInventory2 className="text-2xl text-white" />
                    </div>
                    <div>
                        <h1 className="sm:text-lg lg:text-2xl font-bold text-base-content">
                            Estoque de produtos
                        </h1>
                        <p className="text-sm text-base-content/60">
                            {total} {total === 1 ? 'produto' : 'produtos'} com
                            controle de estoque nesta unidade
                        </p>
                    </div>
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-4">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <div className="flex-1 relative">
                        <MdSearch className="absolute z-10 left-3 top-1/2 -translate-y-1/2 text-base-content/70 text-xl pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Buscar produto pelo nome..."
                            className="input input-bordered w-full pl-10"
                            value={search}
                            onChange={event => {
                                setPage(1)
                                setSearch(event.target.value)
                            }}
                        />
                    </div>
                    {search && (
                        <button
                            type="button"
                            onClick={() => setSearch('')}
                            className="btn btn-ghost btn-sm"
                        >
                            <MdClose className="text-lg" />
                        </button>
                    )}
                    <label className="label cursor-pointer gap-3 justify-start sm:justify-center">
                        <input
                            type="checkbox"
                            className="checkbox checkbox-warning"
                            checked={onlyLow}
                            onChange={event => {
                                setPage(1)
                                setOnlyLow(event.target.checked)
                            }}
                        />
                        <span className="label-text whitespace-nowrap">
                            Só abaixo do mínimo
                        </span>
                    </label>
                </div>
            </div>

            <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm overflow-x-auto">
                {loading ? (
                    <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                        <div className="loading loading-spinner loading-lg text-primary"></div>
                        <p className="text-base-content/60 text-sm">
                            Carregando o estoque...
                        </p>
                    </div>
                ) : items.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[300px] gap-3 p-6 text-center">
                        <MdInventory2 className="text-5xl text-base-content/30" />
                        <p className="text-base-content/70">
                            Nenhum produto com controle de estoque nesta unidade.
                        </p>
                        <p className="text-sm text-base-content/50 max-w-md">
                            O controle é ligado no cadastro do produto, na seção
                            "Estoque" — e vale por filial.
                        </p>
                    </div>
                ) : (
                    <table className="table">
                        <thead>
                            <tr>
                                <th>Produto</th>
                                <th className="text-right">Saldo</th>
                                <th className="text-right">Reservado</th>
                                <th className="text-right">Disponível</th>
                                <th className="text-right">Mínimo</th>
                                <th className="text-right">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => (
                                <tr key={item.catalogId} className="hover">
                                    <td>
                                        <div className="flex flex-col">
                                            <span className="font-medium flex items-center gap-2">
                                                {item.name}
                                                {item.low && (
                                                    <span
                                                        className="badge badge-warning badge-sm gap-1"
                                                        title="No ou abaixo do estoque mínimo"
                                                    >
                                                        <MdWarning className="text-sm" />
                                                        Mínimo
                                                    </span>
                                                )}
                                            </span>
                                            {item.sku && (
                                                <span className="text-xs text-base-content/60">
                                                    SKU {item.sku}
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                    <td className="text-right font-medium">
                                        {item.stockQuantity}
                                    </td>
                                    <td className="text-right">
                                        {item.reserved > 0 ? (
                                            <span
                                                className="text-info"
                                                title="Preso em compras da loja que ainda não fecharam"
                                            >
                                                {item.reserved}
                                            </span>
                                        ) : (
                                            <span className="text-base-content/40">
                                                —
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-right font-semibold">
                                        {item.available}
                                    </td>
                                    <td className="text-right">
                                        {item.minStock ?? (
                                            <span className="text-base-content/40">
                                                —
                                            </span>
                                        )}
                                    </td>
                                    <td>
                                        <div className="flex items-center justify-end gap-1">
                                            <button
                                                className="btn btn-ghost btn-sm gap-1"
                                                onClick={() =>
                                                    setMovementTarget(item)
                                                }
                                                title="Lançar entrada, ajuste, perda ou contagem"
                                            >
                                                <MdSwapVert className="text-lg" />
                                                <span className="hidden lg:inline">
                                                    Movimentar
                                                </span>
                                            </button>
                                            <button
                                                className="btn btn-ghost btn-sm gap-1"
                                                onClick={() =>
                                                    setHistoryTarget(item)
                                                }
                                                title="Ver o extrato do produto"
                                            >
                                                <MdHistory className="text-lg" />
                                                <span className="hidden lg:inline">
                                                    Extrato
                                                </span>
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        className="btn btn-sm"
                        disabled={page <= 1}
                        onClick={() => setPage(current => current - 1)}
                    >
                        Anterior
                    </button>
                    <span className="text-sm text-base-content/70">
                        Página {page} de {totalPages}
                    </span>
                    <button
                        className="btn btn-sm"
                        disabled={page >= totalPages}
                        onClick={() => setPage(current => current + 1)}
                    >
                        Próxima
                    </button>
                </div>
            )}

            {movementTarget && (
                <StockMovementModal
                    item={movementTarget}
                    companyId={user?.companyId}
                    onClose={() => setMovementTarget(null)}
                    onSaved={() => {
                        setMovementTarget(null)
                        load()
                    }}
                />
            )}

            {historyTarget && (
                <StockHistoryModal
                    item={historyTarget}
                    companyId={user?.companyId}
                    onClose={() => setHistoryTarget(null)}
                />
            )}
        </div>
    )
}

/** Lançamento manual: entrada, ajuste, perda, devolução ou contagem. */
function StockMovementModal({
    item,
    companyId,
    onClose,
    onSaved,
}: {
    item: CatalogStockItem
    companyId?: number | null
    onClose: () => void
    onSaved: () => void
}) {
    const [reason, setReason] = useState<CatalogStockMovementReason>('PURCHASE')
    const [quantity, setQuantity] = useState('')
    const [unitCost, setUnitCost] = useState('')
    const [notes, setNotes] = useState('')
    const [saving, setSaving] = useState(false)

    const isInventory = reason === 'INVENTORY'
    const parsedQuantity = parseInt(quantity, 10)

    const preview = (() => {
        if (Number.isNaN(parsedQuantity)) return null
        if (isInventory) return Math.max(0, parsedQuantity)
        if (reason === 'LOSS') {
            return Math.max(0, item.stockQuantity - parsedQuantity)
        }
        return item.stockQuantity + parsedQuantity
    })()

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        if (Number.isNaN(parsedQuantity) || parsedQuantity < 0) {
            toast.error('Informe a quantidade')
            return
        }

        setSaving(true)
        try {
            const response = await createCatalogStockMovement({
                catalogId: item.catalogId,
                companyId: companyId ?? undefined,
                reason,
                quantity: parsedQuantity,
                unitCost: unitCost ? parseCurrencyToCents(unitCost) : undefined,
                notes: notes.trim() || undefined,
            })
            if (response.success) {
                toast.success(
                    `Estoque de "${item.name}" atualizado: ${response.result.previousQuantity} → ${response.result.newQuantity}`
                )
                onSaved()
            } else {
                toast.error(response.error || 'Erro ao lançar o movimento')
            }
        } catch {
            toast.error('Erro ao lançar o movimento')
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog className="modal modal-open">
            <div className="modal-box max-w-lg">
                <h3 className="font-bold text-lg mb-1">Movimentar estoque</h3>
                <p className="text-sm text-base-content/60 mb-5">
                    {item.name} — saldo atual {item.stockQuantity}
                </p>

                <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                    <div className="form-control w-full">
                        <label className="label pb-1" htmlFor="stock-reason">
                            <span className="label-text font-medium">
                                Motivo
                            </span>
                        </label>
                        <select
                            id="stock-reason"
                            className="select select-bordered w-full"
                            value={reason}
                            onChange={event =>
                                setReason(
                                    event.target
                                        .value as CatalogStockMovementReason
                                )
                            }
                        >
                            {MANUAL_STOCK_REASONS.map(value => (
                                <option key={value} value={value}>
                                    {STOCK_MOVEMENT_REASON_LABELS[value]}
                                </option>
                            ))}
                        </select>
                        <label className="label pt-1">
                            <span className="label-text-alt text-base-content/60">
                                {isInventory
                                    ? 'A quantidade informada passa a ser o saldo.'
                                    : reason === 'LOSS'
                                      ? 'A quantidade informada sai do saldo.'
                                      : 'A quantidade informada entra no saldo.'}
                            </span>
                        </label>
                    </div>

                    <div className="form-control w-full">
                        <label className="label pb-1" htmlFor="stock-quantity">
                            <span className="label-text font-medium">
                                {isInventory
                                    ? 'Quantidade contada'
                                    : 'Quantidade'}
                            </span>
                        </label>
                        <input
                            id="stock-quantity"
                            type="number"
                            min={0}
                            className="input input-bordered w-full"
                            value={quantity}
                            onChange={event => setQuantity(event.target.value)}
                            placeholder="0"
                            autoFocus
                        />
                    </div>

                    {reason === 'PURCHASE' && (
                        <div className="form-control w-full">
                            <label className="label pb-1" htmlFor="stock-cost">
                                <span className="label-text font-medium">
                                    Custo unitário
                                </span>
                            </label>
                            <input
                                id="stock-cost"
                                type="text"
                                className="input input-bordered w-full"
                                value={unitCost}
                                onChange={event =>
                                    setUnitCost(currencyFormat(event.target.value))
                                }
                                placeholder="R$ 0,00"
                            />
                            <label className="label pt-1">
                                <span className="label-text-alt text-base-content/60">
                                    Opcional. Fica no extrato, para conferir a nota.
                                </span>
                            </label>
                        </div>
                    )}

                    <div className="form-control w-full">
                        <label className="label pb-1" htmlFor="stock-notes">
                            <span className="label-text font-medium">
                                Observação
                            </span>
                        </label>
                        <input
                            id="stock-notes"
                            type="text"
                            className="input input-bordered w-full"
                            value={notes}
                            onChange={event => setNotes(event.target.value)}
                            placeholder="Nota fiscal, motivo da perda, quem contou..."
                            maxLength={500}
                        />
                    </div>

                    {preview !== null && (
                        <div className="alert alert-info text-sm">
                            <span>
                                Saldo depois do lançamento:{' '}
                                <strong>{preview}</strong>
                            </span>
                        </div>
                    )}

                    <div className="modal-action">
                        <button
                            type="button"
                            className="btn btn-ghost"
                            onClick={onClose}
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={saving}
                        >
                            {saving && (
                                <span className="loading loading-spinner loading-sm"></span>
                            )}
                            Lançar
                        </button>
                    </div>
                </form>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}

/** Extrato do produto: o que explica o saldo de hoje. */
function StockHistoryModal({
    item,
    companyId,
    onClose,
}: {
    item: CatalogStockItem
    companyId?: number | null
    onClose: () => void
}) {
    const [movements, setMovements] = useState<CatalogStockMovement[]>([])
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const load = async () => {
            setLoading(true)
            try {
                const response = await getCatalogStockMovements({
                    companyId: companyId ?? undefined,
                    catalogId: item.catalogId,
                    take: 100,
                })
                if (response.success) {
                    setMovements(response.result.data)
                } else {
                    toast.error(response.error || 'Erro ao carregar o extrato')
                }
            } catch {
                toast.error('Erro ao carregar o extrato')
            } finally {
                setLoading(false)
            }
        }
        load()
    }, [item.catalogId, companyId])

    return (
        <dialog className="modal modal-open">
            <div className="modal-box max-w-3xl">
                <h3 className="font-bold text-lg mb-1">Extrato do estoque</h3>
                <p className="text-sm text-base-content/60 mb-5">
                    {item.name} — saldo atual {item.stockQuantity}
                </p>

                {loading ? (
                    <div className="flex items-center justify-center min-h-[200px]">
                        <div className="loading loading-spinner loading-lg text-primary"></div>
                    </div>
                ) : movements.length === 0 ? (
                    <div className="flex flex-col items-center justify-center min-h-[200px] gap-2 text-center">
                        <MdHistory className="text-4xl text-base-content/30" />
                        <p className="text-base-content/70">
                            Nenhum movimento registrado ainda.
                        </p>
                        <p className="text-sm text-base-content/50 max-w-md">
                            O extrato começa a partir da primeira entrada, venda ou
                            ajuste feito pelo sistema.
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto max-h-[60vh]">
                        <table className="table table-sm table-pin-rows">
                            <thead>
                                <tr>
                                    <th>Quando</th>
                                    <th>Motivo</th>
                                    <th className="text-right">Quantidade</th>
                                    <th className="text-right">Saldo</th>
                                    <th>Quem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {movements.map(movement => (
                                    <tr key={movement.id}>
                                        <td className="whitespace-nowrap">
                                            {new Date(
                                                movement.createdAt
                                            ).toLocaleString('pt-BR')}
                                        </td>
                                        <td>
                                            <div className="flex flex-col">
                                                <span>
                                                    {
                                                        STOCK_MOVEMENT_REASON_LABELS[
                                                            movement.reason
                                                        ]
                                                    }
                                                </span>
                                                {movement.notes && (
                                                    <span className="text-xs text-base-content/60">
                                                        {movement.notes}
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td
                                            className={`text-right font-medium ${
                                                movement.type === 'IN'
                                                    ? 'text-success'
                                                    : 'text-error'
                                            }`}
                                        >
                                            {movement.type === 'IN' ? '+' : '−'}
                                            {movement.quantity}
                                        </td>
                                        <td className="text-right whitespace-nowrap text-base-content/70">
                                            {movement.previousQuantity} →{' '}
                                            <strong className="text-base-content">
                                                {movement.newQuantity}
                                            </strong>
                                        </td>
                                        <td className="whitespace-nowrap">
                                            {movement.user?.name ?? (
                                                <span className="text-base-content/50">
                                                    Sistema
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn" onClick={onClose}>
                        Fechar
                    </button>
                </div>
            </div>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose}>
                    fechar
                </button>
            </form>
        </dialog>
    )
}
